import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExportButton } from "@/components/ExportButton";
import { api, fmtDay, TIMELINE_COLORS } from "@/lib/api";
import { useFreshest } from "@/lib/freshest";

const SHORT = {
  "Model Development": "Model dev.",
  "Baseline Signed Off": "Baseline",
  "Hot Trials": "Hot trials",
  "Go-Live": "Go-Live",
  "Assessment Period Completed": "Assessment",
  "Final Signoff & Handover": "Signoff",
};

const Cell = ({ m }) => {
  const tone = TIMELINE_COLORS[m.state];
  const slip = m.slip_days;
  return (
    <td className="border-l border-border px-2 py-2 align-top" data-testid={`ms-${m.stage}`}>
      <div className="font-data text-[11px] font-semibold" style={{ color: tone }}>
        {m.actual_on ? fmtDay(m.actual_on) : m.expected_on ? fmtDay(m.expected_on) : "—"}
      </div>
      <div className="text-[10px] text-muted-foreground">
        {m.actual_on
          ? slip > 0
            ? `${slip}d late`
            : "done"
          : m.expected_on
            ? slip > 0
              ? `${slip}d over`
              : `in ${Math.abs(slip)}d`
            : "awaited"}
      </div>
    </td>
  );
};

const Chip = ({ state, label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`timeline-chip-${state}`}
    className={`border px-3 py-2 text-left transition-colors duration-200 ${
      active ? "border-foreground bg-muted" : "border-border bg-card hover:bg-muted"
    }`}
  >
    <div className="font-data text-lg font-bold" style={{ color: TIMELINE_COLORS[state] }}>
      {count}
    </div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
  </button>
);

export const TimelineTracker = ({ plant, onOpen }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState("");
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("all");

  const { start, isFresh } = useFreshest();

  const load = useCallback(() => {
    // Only the newest request may put rows on the screen.
    const ticket = start();
    api
      .get("/deepdive/timeline", {
        params: { plant: plant === "all" ? "" : plant, state, q, term: term === "all" ? "" : term },
      })
      .then((r) => isFresh(ticket) && setData(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, state, q, term]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  if (!data)
    return (
      <div className="mt-8 flex h-32 items-center justify-center border border-border bg-card">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div className="mt-10" data-testid="timeline-tracker">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-primary" />
        <div className="label-caps">Expected against actual</div>
      </div>
      <h2 className="font-display mt-1 text-base font-bold sm:text-lg">
        Which use cases are running to time, and which are overshooting
      </h2>
      <p className="mt-2 max-w-3xl text-xs text-muted-foreground">
        Milestone by milestone, the date a unit expected to get there against the date it actually
        did. A use case is counted as slipping the day it passes an expected date without the
        milestone being reached.
      </p>

      <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-6">
        {data.state_order.map((s) => (
          <Chip
            key={s}
            state={s}
            label={data.states[s]}
            count={data.counts[s] || 0}
            active={state === s}
            onClick={() => setState(state === s ? "" : s)}
          />
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="timeline-search"
            placeholder="Search a use case, shop or owner…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 rounded-none bg-card pl-9"
          />
        </div>
        <Select value={term} onValueChange={setTerm}>
          <SelectTrigger className="h-11 w-[170px] rounded-none bg-card" data-testid="timeline-term">
            <SelectValue placeholder="Term" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            <SelectItem value="all">Short and long term</SelectItem>
            <SelectItem value="Short-Term">Short-Term</SelectItem>
            <SelectItem value="Long-Term">Long-Term</SelectItem>
          </SelectContent>
        </Select>
        <ExportButton
          path="/deepdive/timeline/export"
          params={{
            plant: plant === "all" ? "" : plant,
            state,
            q,
            term: term === "all" ? "" : term,
          }}
          testId="export-timeline"
          label="Download this view"
        />
      </div>

      <div className="mt-4 overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[1100px] text-left text-xs">
          <thead className="bg-muted">
            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-3">Use case</th>
              <th className="px-3 py-3">Where it stands</th>
              {data.milestones.map((m) => (
                <th key={m} className="border-l border-border px-2 py-3">
                  {SHORT[m] || m}
                </th>
              ))}
              <th className="border-l border-border px-3 py-3">Next date</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.slice(0, 60).map((r) => (
              <tr
                key={r.id}
                data-testid={`timeline-row-${r.code}`}
                onClick={() => onOpen?.(r.id)}
                className="cursor-pointer border-t border-border transition-colors duration-200 hover:bg-muted"
              >
                <td className="px-3 py-3">
                  <div className="font-data text-[10px] text-muted-foreground">
                    {r.code} · {r.plant}
                    {r.is_target_value && (
                      <span className="ml-2 border border-primary px-1 text-[9px] uppercase text-primary">
                        Target value
                      </span>
                    )}
                  </div>
                  <div className="font-semibold">{r.title}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.dept_shop || "—"} · {r.phase || "—"}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <span
                    className="border px-2 py-1 text-[10px] font-bold uppercase tracking-wider"
                    style={{
                      color: TIMELINE_COLORS[r.overall],
                      borderColor: TIMELINE_COLORS[r.overall],
                    }}
                    data-testid={`timeline-state-${r.code}`}
                  >
                    {data.states[r.overall]}
                  </span>
                  {r.worst_slip_days > 0 && (
                    <div className="font-data mt-1 text-[10px] text-[hsl(var(--at-risk))]">
                      {r.worst_slip_days} day(s) beyond plan
                    </div>
                  )}
                  {r.overall === "paused" && r.hold_reason && (
                    <div className="mt-1 text-[10px] text-muted-foreground">{r.hold_reason}</div>
                  )}
                </td>
                {r.rows.map((m) => (
                  <Cell key={m.stage} m={m} />
                ))}
                <td className="border-l border-border px-3 py-3">
                  <div className="font-data text-[11px]">
                    {r.next_expected_on ? fmtDay(r.next_expected_on) : "—"}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.next_stage || "no date given"}
                  </div>
                </td>
              </tr>
            ))}
            {!data.rows.length && (
              <tr>
                <td colSpan={data.milestones.length + 3} className="px-3 py-10 text-center text-muted-foreground">
                  No use case matches what you have picked.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span data-testid="timeline-shown">
          Showing {Math.min(data.rows.length, 60)} of {data.rows.length} use case(s), the ones
          furthest behind first. Narrow it down with the boxes above.
        </span>
        <span>
          {data.totals.with_dates} of {data.totals.use_cases} use case(s) have given an expected
          date so far.
        </span>
      </div>
    </div>
  );
};
